import { Element, ElementStyle } from 'store/theme';
import {
  useModuleVersionOnCreation,
  useCompareModuleVersionOnCreationWith,
} from 'services/useModuleVersionOnCreation';

interface IUpdateGlobalFontStylesFromTheme {
  theme;
  textColorFromInteractionOrTopic?: string;
  headingsStyles?: () => string;
}

const defaultTextSize = 16;
const defaultLineHeight = 24;

export const elementInitialFontsizeOffsets = {
  h1: 3.5,
  h2: 2.5,
  h3: 2,
  h4: 1.7,
  h5: 1.2,
  h6: 1,
  p: 1,
};

export const elementResponsiveFontsizePXValues = {
  h1: [56, 40, 32],
  h2: [40, 32, 24],
  h3: [32, 28, 20],
  h4: [27, 24, 16],
  h5: [19.2, 16, 16],
  h6: [16, 16, 16],
};

export const elementResponsiveFontsizePXValueFns = {
  h1: [fS => fS / 1, fS => fS / 1.4, fS => fS / 1.75],
  h2: [fS => fS / 1, fS => fS / 1.25, fS => fS / 1.67],
  h3: [fS => fS / 1, fS => fS / 1.14, fS => fS / 1.67],
  h4: [fS => fS / 1, fS => fS / 1.125, fS => fS / 1.6875],
  h5: [fS => fS / 1, fS => fS / 1.2, fS => fS / 1.2],
  h6: [fS => fS / 1, fS => fS / 1, fS => fS / 1],
};

export const elementInitialLineHeightOffsets = {
  h1: 3,
  h2: 2.3,
  h3: 2,
  h4: 1.73,
  h5: 1.3,
  h6: 1.06,
  p: 1,
};

export const elementResponsiveLineHeightPXValues = {
  h1: [72, 52, 46],
  h2: [55, 44, 36],
  h3: [48, 40, 32],
  h4: [42, 36, 28],
  h5: [31, 25, 24],
  h6: [25, 22, 24],
};

export const elementResponsiveLineHeightPXValueFns = {
  h1: [fS => fS / 1, fS => fS / 1.384, fS => fS / 1.57 || 2.25],
  h2: [fS => fS / 1, fS => fS / 1.25, fS => fS / 1.53 || 1.71875],
  h3: [fS => fS / 1, fS => fS / 1.2, fS => fS / 1.5],
  h4: [fS => fS / 1, fS => fS / 1.1667, fS => fS / 1.5 || 1.3125],
  h5: [fS => fS / 1, fS => fS / 1.24, fS => fS / 1.29 || 1.55],
  h6: [fS => fS / 1, fS => fS / 1.136, fS => fS / 1.04 || 1.25],
};

export function getInitialFontSize(element) {
  const elementInitialFontsizeOffset =
    elementInitialFontsizeOffsets[element] || 1;

  return 16 * elementInitialFontsizeOffset;
}

export type LineHeightOffsets = keyof typeof elementInitialLineHeightOffsets;

export function getInitialLineHeight(
  element: LineHeightOffsets,
  lineHeight = 24
) {
  return lineHeight * elementInitialLineHeightOffsets[element] || 1;
}

export function updateGlobalFontStylesFromTheme({
  theme,
  textColorFromInteractionOrTopic: textColor,
  headingsStyles,
}: IUpdateGlobalFontStylesFromTheme) {
  const typeSettings = theme?.typeSettings || {};

  const {
    fontFamily = 'Poppins',
    fontSize = defaultTextSize,
    bold = false,
    italic = false,
  } = typeSettings || {};

  // console.log({ typeSettings });

  const settingsType = typeSettings?.type || 'basic';

  let lineHeight = typeSettings?.lineHeight;
  //
  // NaN is falsey. yep, but wanting to check it explicitly
  if (!lineHeight || isNaN(lineHeight)) {
    lineHeight = defaultLineHeight;
  }

  // console.log(
  //   `%clineHeight as it cascades from 'basic' to 'advanced' :\nglobal ? ${lineHeight ||
  //     defaultLineHeight}px\np.lineheight ? ${
  //     typeSettings?.elementStyles?.p?.lineHeight
  //   }`,
  //   'color: #fff000; background: #000;'
  // { textColor }, typeSettings?.color
  // );

  const elementStyles = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].reduce(
    (acc, curr, index) => {
      const { typeSettings = {} } = theme;

      const currentElementStyle =
        theme?.typeSettings?.elementStyles?.[curr] || {};

      let fontFamily =
        currentElementStyle?.fontFamily ||
        typeSettings?.fontFamily ||
        'Poppins';

      let fontSize =
        currentElementStyle?.fontSize ||
        typeSettings?.fontSize * elementInitialFontsizeOffsets[curr];

      // load default
      // 'required' to load it loosely and not bake it into the typesettings on boot
      // so that changes to the high level basic fontSizes ( S M L ) will update
      //
      if (!fontSize || isNaN(fontSize)) {
        fontSize = 16 * elementInitialFontsizeOffsets[curr];
      }

      // if !lineHeight then we'll fall back to 'normal' in the settings that follow. . .
      let lineHeight =
        currentElementStyle?.lineHeight ||
        (typeSettings?.fontSize / 16) *
          24 *
          elementInitialLineHeightOffsets[curr];

      return `
    ${acc}
    ${curr} {
      font-family: ${fontFamily} !important;
     
      font-size: ${fontSize}px !important;

      line-height: ${
        lineHeight && !isNaN(lineHeight) ? lineHeight + 'px' : 'normal'
      };

      ${currentElementStyle?.bold ? 'font-weight: 700;' : 'font-weight: 300;'}

      ${
        currentElementStyle?.italic
          ? 'font-style: italic;'
          : 'font-style: normal;'
      }
   
      color: ${textColor || currentElementStyle?.color};

      @media(max-width: 767px) {
        font-size: ${elementResponsiveFontsizePXValueFns?.[curr]?.[1](
          fontSize
        )}px !important;
          
        line-height: ${
          lineHeight && !isNaN(lineHeight)
            ? Math.round(
                elementResponsiveLineHeightPXValueFns?.[curr]?.[1](lineHeight)
              ) + 'px'
            : 'normal'
        };
      }

      @media(max-width: 480px) {
        font-size: ${elementResponsiveFontsizePXValueFns?.[curr]?.[2](
          fontSize
        )}px !important;

          line-height: ${
            lineHeight && !isNaN(lineHeight)
              ? Math.round(
                  elementResponsiveLineHeightPXValueFns?.[curr]?.[2](lineHeight)
                ) + 'px'
              : 'normal'
          };
      }
    }
    `.replace(/\n/g, '');
    },
    ''
  );

  //

  return `
  .chameleon-text {
   
    color: ${textColor || typeSettings?.color};

    ${elementStyles}

    p:not(h1 p, h2 p, h3 p, h4 p, h5 p, h6 p, .chameleon-certificate-text *) {

      font-family: ${typeSettings?.elementStyles?.p?.fontFamily ||
        fontFamily ||
        'Poppins'};

      font-size: ${typeSettings?.elementStyles?.p?.fontSize || fontSize}px;

      line-height: ${typeSettings?.elementStyles?.p?.lineHeight ||
        lineHeight ||
        24}px;

        font-weight: ${typeSettings?.elementStyles?.p?.bold || bold ? 700 : ''};
        font-style: ${
          typeSettings?.elementStyles?.p?.italic || italic ? 'italic' : 'normal'
        };

      color: ${textColor || typeSettings?.elementStyles?.p?.color};
    
    }

    li:not(h1 li, h2 li, h3 li, h4 li, h5 li, h6 li) {
      font-family: ${typeSettings?.elementStyles?.p?.fontFamily ||
        fontFamily ||
        'Poppins'};

      font-size: ${typeSettings?.elementStyles?.p?.fontSize || fontSize}px;

      line-height: ${typeSettings?.elementStyles?.p?.lineHeight ||
        lineHeight}px;

        font-weight: ${typeSettings?.elementStyles?.p?.bold || bold ? 700 : ''};
        font-style: ${
          typeSettings?.elementStyles?.p?.italic || italic ? 'italic' : 'normal'
        };
    }

  }

  li > span {
    // color: ${textColor};
  }
  
  `;
}
